import React from 'react';
import { Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { User, Phone } from 'lucide-react';
import { TableAction } from '@/components';
import { useResendStore } from '@/stores/useResendStore';
import { InventoryOrder } from '@/types/inventory';

type AvailableCarrier = InventoryOrder['available_carriers_detail'][number];

interface ResendSummaryRow {
  key: string;
  uid: string;
  platform_code: string;
  full_name: string;
  phone: string;
  selectedCarrier: AvailableCarrier | null;
  fee: number | null;
}

const ResendStep3: React.FC = () => {
  const { t } = useTranslation();
  // Step 2 of 2 — the carrier is never named here (owner 2026-10-28). Its
  // recommended entry only supplies the fee estimate.
  const { selectedOrders, orderOverrides } = useResendStore();

  const tableData: ResendSummaryRow[] = selectedOrders.map((order) => {
    const override = orderOverrides[order.uid];
    const activeSlug = override?.carrier_slug ?? order.recommended_carrier_slug ?? null;
    const carriers = order.available_carriers_detail ?? [];
    const selectedCarrier =
      activeSlug != null
        ? carriers.find((c) => c.carrier_slug === activeSlug) ?? null
        : null;

    return {
      key: order.uid,
      uid: order.uid,
      platform_code: order.platform_code,
      full_name: override?.customer_data?.full_name ?? order.full_name,
      phone: override?.customer_data?.phone ?? order.phone,
      selectedCarrier,
      fee: selectedCarrier?.shipping_fee ?? null,
    };
  });

  const totalFee = tableData.reduce((acc, row) => acc + (row.fee ?? 0), 0);

  const columns: ColumnsType<ResendSummaryRow> = [
    {
      title: String(t('original_order')),
      dataIndex: 'platform_code',
      key: 'platform_code',
      render: (text: string) => (
        <span className='text-sm font-medium text-sd-foreground'>{text || '—'}</span>
      ),
    },
    {
      title: String(t('customer')),
      key: 'customer',
      render: (_: unknown, record: ResendSummaryRow) => (
        <Flex vertical gap={2}>
          <Flex align='center' gap={6}>
            <User size={14} className='text-gray-400 shrink-0' strokeWidth={1.5} />
            <span className='text-sm font-medium text-sd-foreground'>{record.full_name}</span>
          </Flex>
          <Flex align='center' gap={6}>
            <Phone size={12} className='text-sd-muted-foreground shrink-0' strokeWidth={1.5} />
            <span className='text-xs text-sd-muted-foreground'>{record.phone}</span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: String(t('shipment_payment')),
      key: 'fee',
      render: (_: unknown, record: ResendSummaryRow) => (
        <span className='text-sm text-sd-foreground'>
          {record.fee != null && isFinite(record.fee)
            ? `${Math.round(record.fee).toLocaleString()} ${String(t('iqd'))}`
            : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className='border border-solid border-sd-border rounded-2xl bg-white p-6'>
      <h2 className='text-base font-semibold text-sd-foreground mt-0 mb-1'>
        {String(t('resend_step3_title'))}
      </h2>
      <p className='text-sm text-sd-muted-foreground mt-0 mb-4'>
        {String(t('review_edit_before_finalizing'))}
      </p>

      <TableAction<ResendSummaryRow>
        columns={columns}
        data={tableData}
        pageSize={tableData.length || 10}
        hidePagination
        customClassName='import-table'
      />

      {tableData.length > 0 && (
        <div className='mt-4 flex justify-end'>
          <div className='rounded-xl border border-solid border-sd-border bg-sd-muted/30 px-5 py-3'>
            <span className='text-sm font-semibold text-sd-foreground'>
              {String(t('total'))}: {Math.round(totalFee).toLocaleString()} {String(t('iqd'))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResendStep3;
